import React, { useEffect, useState } from "react";
import SettingsIcon from "../../assets/icons/service_entries.svg2";
import SettingsLinks from "./SettingsLinks";
import GeneralSettingsIcon from "../../assets/icons/settings.svg2";
import {
  Col,
  FloatingLabel,
  Form,
  FormLabel,
  InputGroup,
  Row,
} from "react-bootstrap";
import HelpButton from "../HelpContent/HelpButton";
import PersonalDetailsIcon from "../../assets/icons/personal_details.svg2";
import CompanyDetailsIcon from "../../assets/icons/briefcase.svg2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-regular-svg-icons";
import PageHeader from "../Common/PageHeader";
import MyIcon from "../Common/MyIcon";
import Subheading from "../Common/Subheading";
import SecuritySettingsIcon from "../../assets/icons/security.svg2";
import { faRotate } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import MyCollapse from "../Common/MyCollapse";
import {
  useGetSecuritySettingsDataQuery,
  useUpdateSecuritySettingsDataMutation,
} from "../store/slices/settingsSlice";
import { toast } from "react-toastify";
import { isUserPermitted, pageProps } from "../helpers/pageProps";

function SecuritySettings() {
  const canEdit = isUserPermitted("edit_system_setting");
  const [securitySettings, setSecuritySettings] = useState({});
  const { data } = useGetSecuritySettingsDataQuery();
  const [errors, setErrors] = useState({});
  const [saveData] = useUpdateSecuritySettingsDataMutation();

  const handleSave = async () => {
    setErrors({});
    const res = await saveData({
      ...securitySettings,
      tf_frequency: securitySettings.tf_frequency,
      frequency: securitySettings.tf_frequency,
      code_attempts: securitySettings.tf_code_attempts,
      code_expire: securitySettings.tf_expire_minutes,
      force_authenticator:
        securitySettings.force_authenticator === "Y" ? "Y" : undefined,
    });
    if (res.data.status === "error") {
      toast.error(res.data.title);
      setErrors(res.data.errors);
      return;
    }
    toast.success("Settings saved successfully");
  };
  useEffect(() => {
    if (!data) return;
    const savedSecuritySettings = pageProps.securitySetting ?? {};
    setSecuritySettings({
      ...data,
      ...savedSecuritySettings,
      force_authenticator:
        savedSecuritySettings.force_authenticator ?? data.force_authenticator ?? "N",
    });
  }, [data]);

  const handleSecurityChange = (e) =>
    setSecuritySettings((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

  const handleToggle = (name) =>
    setSecuritySettings((prev) => ({
      ...prev,
      [name]: prev[name] === "Y" ? "N" : "Y",
    }));

  return (
    <div className="mb-5">
      <Subheading
        Icon={SecuritySettingsIcon}
        heading="Security Settings"
        subheading="Define the system security you want"
      />

      <fieldset disabled={!canEdit} className="border-0 p-0 m-0">
      <Row xs={1} xl={2} className="my-3">
        <Col>
          <div className="bg-purple-gradient-darker-reverse rounded-4 p-3 ">
            <>
              <h5 className=" d-flex align-items-center">
                <MyIcon Icon={SecuritySettingsIcon} className="me-2" />
                <span className="ms-2">
                  <span className="fw-bold me-2"> Security Settings:</span>
                  <HelpButton helpKey="settings-security-settings" />
                  <div className="small text-secondary">
                    Manage the security settings for your account
                  </div>
                </span>
              </h5>
            </>

            <hr className="my-3 text-secondary" />
            <Form>
              <div className="row">
                <div className="col-6">
                  <div className="mx-0 mb-2 bg-white align-items-center rounded-3 ">
                    <div className="position-relative">
                      <FloatingLabel label="Set Session Timeout">
                        <Form.Control
                          name="system_timeout"
                          placeholder="Set Session Timeout"
                          onChange={handleSecurityChange}
                          value={securitySettings.system_timeout}
                        />
                      </FloatingLabel>
                      <div
                        id="hours"
                        className="me-2 position-absolute top-50 end-0 translate-middle-y"
                      >
                        <span className="me-2"> Hours</span>
                        <HelpButton helpKey="settings-security-settings-set-session-timeout" />
                      </div>
                    </div>
                  </div>
                  {errors.system_timeout && (
                    <div className="alert alert-danger">
                      {errors.system_timeout}
                    </div>
                  )}
                </div>
                <div className="col-6">
                  <div className="mx-0 mb-2 bg-white align-items-center rounded-3 ">
                    <div className="position-relative">
                      <FloatingLabel label="Force Password Change">
                        <Form.Control
                          name="password_change"
                          placeholder="Force Password Change"
                          onChange={handleSecurityChange}
                          value={securitySettings.password_change}
                        />
                      </FloatingLabel>
                      <div
                        id="days"
                        className="me-2 position-absolute top-50 end-0 translate-middle-y"
                      >
                        <span className="me-2"> Days</span>
                        <HelpButton helpKey="settings-security-settings-force-password-change" />
                      </div>
                    </div>
                  </div>
                  {errors.password_change && (
                    <div className="alert alert-danger">
                      {errors.password_change}
                    </div>
                  )}
                </div>
                <div className="col-6">
                  <div className="mx-0 mb-2 bg-white align-items-center rounded-3 ">
                    <div className="position-relative">
                      <FloatingLabel label="Lock Failed Login">
                        <Form.Control
                          placeholder="Lock Failed Login"
                          name="login_attempts"
                          onChange={handleSecurityChange}
                          value={securitySettings.login_attempts}
                        />
                      </FloatingLabel>
                      <div
                        id="attempts"
                        className="me-2 position-absolute top-50 end-0 translate-middle-y"
                      >
                        <span className="me-2">Attempts</span>
                        <HelpButton helpKey="settings-security-settings-lock-failed-login" />
                      </div>
                    </div>
                  </div>
                  {errors.login_attempts && (
                    <div className="alert alert-danger">
                      {errors.login_attempts}
                    </div>
                  )}
                </div>
                <div className="fw-bold mt-3">
                  Boomerang Administrative Access
                </div>
                <hr className="my-3 text-secondary" />

                <div>
                  Boomerang administrative access allows Boomerang
                  administrators to impersonate your system users to assist with
                  the investigation of support requests and / or use of the
                  Boomerang products. The user being impersonated will be asked
                  to provide their authorisation to Boomerang before access is
                  allowed. Please note that on occasions where Boomerang
                  Administrative Access is deemed critical to the operation of
                  your services, a Boomerang user may be given internal
                  authorisation to impersonate one of your system users. Key
                  actions carried out when impersonating are recorded.
                </div>
                <Form.Check
                  type="switch"
                  label="Allow Boomerang administrative access"
                  className="p-3 ps-0 my-2 fs-5"
                />
              </div>
            </Form>
          </div>
        </Col>
        <Col>
          <div className="bg-purple-gradient-darker-reverse rounded-4 p-3 ">
            <>
              <h5 className=" align-items-center">
                <span className="fw-bold me-2">2-factor Authentication</span>{" "}
                <HelpButton helpKey="settings-security-settings-2-factor-authentication" />
                <div className="small text-secondary">
                  Enable Two Factor Authentication
                </div>
              </h5>
            </>

            <InputGroup>
              <hr className="my-3 text-secondary" />
              <Form.Label>Force users to set up an authenticator app</Form.Label>
              <Form.Check
                type="switch"
                id="force-authenticator-switch"
                name="force_authenticator"
                checked={securitySettings.force_authenticator === "Y"}
                onChange={() => handleToggle("force_authenticator")}
              />
              <div className="small text-secondary w-100 mb-3">
                Users without an authenticator app will be prompted to set one up. Users with one configured will use it instead of SMS/email 2FA.
              </div>
            </InputGroup>

            <InputGroup>
              <hr className="my-3 text-secondary" />
              <Form.Label>Enable</Form.Label>
              <Form.Check // prettier-ignore
                type="switch"
                id="two-factors-switch"
                name="two_factors"
                checked={securitySettings.two_factors === "Y"}
                onChange={() => handleToggle("two_factors")}
              />
            </InputGroup>

            {securitySettings.two_factors === "Y" && (
              <Row>
                <div className="col-6">
                  <div className="mx-0 mb-2 bg-white align-items-center rounded-3">
                    <div className="position-relative">
                      <FloatingLabel label="Frequency" className="me-4">
                        <Form.Select
                          value={securitySettings.tf_frequency}
                          onChange={handleSecurityChange}
                          name="tf_frequency"
                          placeholder="New User"
                          className="border-0"
                        >
                          {[
                            {
                              value: "new_user",
                              label: "New User(Initial login)",
                            },
                            { value: "daily", label: "Daily" },
                            { value: "weekly", label: "Weekly" },
                            { value: "monthly", label: "Monthly " },
                            { value: "quarterly", label: "Quarterly" },
                            { value: "yearly", label: "Yearly " },
                            ...(securitySettings.force_authenticator === "Y" ||
                            securitySettings.tf_frequency === "totp_every_login"
                              ? [
                                  {
                                    value: "totp_every_login",
                                    label: "Every Login (Authenticator App)",
                                  },
                                ]
                              : []),
                          ].map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </Form.Select>
                      </FloatingLabel>
                      <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                        {" "}
                        <HelpButton helpKey="settings-security-settings-2-factor-authentication-frequency" />
                      </div>
                    </div>
                  </div>
                  {errors.frequency && (
                    <div className="alert alert-danger">
                      {errors.frequency}
                    </div>
                  )}
                </div>
                <div className="col-6">
                  <div className="mx-0 mb-2 bg-white align-items-center rounded-3">
                    <div className="position-relative">
                      <FloatingLabel label="Lock User Account After">
                        <Form.Control
                          className="border-0"
                          placeholder="Lock User Account After"
                          name="tf_code_attempts"
                          onChange={handleSecurityChange}
                          value={securitySettings.tf_code_attempts}
                        />
                      </FloatingLabel>
                      <div
                        id="invalidAttempts"
                        className="me-2 position-absolute top-50 end-0 translate-middle-y"
                      >
                        <span className="me-2"> Invalid Attempts</span>{" "}
                        <HelpButton helpKey="settings-security-settings-2-factor-authentication-invalid-attempts" />
                      </div>
                    </div>
                    {errors.code_attempts && (
                      <div className="alert alert-danger">
                        {errors.code_attempts}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-6">
                  <div className="mx-0 mb-2 bg-white align-items-center rounded-3  ">
                    <div className="position-relative">
                      <FloatingLabel label="Security Code Expire After">
                        <Form.Control
                          className="border-0"
                          placeholder="securityCodeEA"
                          name="tf_expire_minutes"
                          onChange={handleSecurityChange}
                          value={securitySettings.tf_expire_minutes}
                        />
                      </FloatingLabel>
                      <div
                        id="minutes"
                        className="me-2 position-absolute top-50 end-0 translate-middle-y"
                      >
                        <span className="me-2"> Minutes</span>{" "}
                        <HelpButton helpKey="settings-security-settings-2-factor-authentication-security-Code-EA" />
                      </div>
                    </div>
                    {errors.code_resend && (
                      <div className="alert alert-danger">
                        {errors.code_resend}
                      </div>
                    )}
                  </div>
                </div>
              </Row>
            )}
          </div>
        </Col>
      </Row>
      <hr className="my-5 text-secondary" />
      <div className="d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-colored-reverse rounded-pill fw-bold "
          onClick={handleSave}
          disabled={!canEdit}
        >
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          SAVE SETTINGS
        </button>
      </div>
      </fieldset>
    </div>
  );
}

export default SecuritySettings;
